// CTSK-170 Part B/8 — Access-request summary card.
//
// Compact scan-friendly card that renders above the tab strip on any
// document tagged `nest/external-clients`. Surfaces the key state
// (role handle + description, client kind, request timestamp, PAT
// prefix, state badge) that operators need at-a-glance without
// scrolling through the full server-composed body (PreviewPane) or
// the full 30+ CP list (Details tab).
//
// Does NOT expose any actions here — approve / deny / change-role
// live in the Nest UI (ExternalClientsNest.tsx). This card is a
// read-only summary that pairs with the Nest's row list.
//
// Guard: only renders when the doc carries `nest/external-clients`.
// Every other doc-detail render skips this component entirely.

import { KeyRound, Bot, Webhook, Cpu, Wrench, Check, X, Clock, RotateCw, AlertTriangle, type LucideIcon } from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────

type AccessState = 'pending' | 'approved' | 'denied' | 'revoked' | 'elevated' | 'unknown';

export interface AccessRequestSummaryCardProps {
  /** Tags on the doc, as plain strings. Component derives access-
   *  state and client-kind from the tag set. */
  tagNames: string[];
  /** Doc's custom properties bag (snake_case keys per the wire shape). */
  customProperties: Record<string, unknown>;
  /** Optional onNavigate for the "See in Nest" chip link. */
  onNavigate?: (path: string) => void;
}

// ─── Config ──────────────────────────────────────────────────────────

const ACCESS_STATE_COLOR: Record<AccessState, string> = {
  pending:  '#F59E0B',
  approved: '#22C55E',
  denied:   '#EF4444',
  revoked:  '#6B7280',
  elevated: '#8B5CF6',
  unknown:  '#94A3B8',
};

const ACCESS_STATE_ICON: Record<AccessState, LucideIcon> = {
  pending:  Clock,
  approved: Check,
  denied:   X,
  revoked:  X,
  elevated: RotateCw,
  unknown:  AlertTriangle,
};

const CLIENT_KIND_ICON: Record<string, LucideIcon> = {
  'claude-mcp-session': Bot,
  'webhook-consumer':   Webhook,
  'ci-bot':             Cpu,
  'external-mcp':       Cpu,
  'human-tool':         Wrench,
};

const CLIENT_KIND_LABEL: Record<string, string> = {
  'claude-mcp-session': 'Claude MCP session',
  'webhook-consumer':   'Webhook consumer',
  'ci-bot':             'CI bot',
  'external-mcp':       'External MCP',
  'human-tool':         'Human tool',
};

// ─── Helpers ─────────────────────────────────────────────────────────

/** Derive the access state from tags. `access/elevated` is treated as
 *  a modifier over `access/approved` — if both are present, the badge
 *  shows "approved · elevated" and uses the elevated color. */
function deriveState(tagNames: string[]): {
  state: AccessState;
  isElevated: boolean;
} {
  const has = (n: string) => tagNames.includes(n);
  const isElevated = has('access/elevated');
  if (has('access/pending'))  return { state: 'pending', isElevated };
  if (has('access/approved')) return { state: 'approved', isElevated };
  if (has('access/denied'))   return { state: 'denied', isElevated };
  if (has('access/revoked'))  return { state: 'revoked', isElevated };
  return { state: 'unknown', isElevated };
}

/** Coerce a CP value to a display string. `null`/`undefined`/empty → em-dash. */
function displayCp(v: unknown): string {
  if (v === undefined || v === null) return '—';
  if (typeof v === 'string') return v.trim() || '—';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

/** CTSK-193 (2026-07-22): actor label — prefer server-enriched
 *  display_name from the users table (added in CTSK-186); fall back to
 *  the raw user_id ULID (truncated + monospaced) if enrichment missing.
 *  Returns null when neither field is populated — caller decides
 *  whether to render "by null" or omit the "by X" clause entirely. */
function displayActor(displayName: unknown, userId: unknown): string | null {
  if (typeof displayName === 'string' && displayName.trim().length > 0) {
    return displayName.trim();
  }
  if (typeof userId === 'string' && userId.trim().length > 0) {
    // Truncate to first 8 chars for compactness; hover would show full.
    return userId.slice(0, 8) + '…';
  }
  return null;
}

/** Format a timestamp as "N min/hours/days ago", with the ISO date as
 *  a hover-friendly title for exact timestamps. */
function relativeTime(iso: unknown): { rel: string; abs: string } {
  if (typeof iso !== 'string' || iso.length === 0) return { rel: '—', abs: '—' };
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return { rel: iso, abs: iso };
  const now = Date.now();
  const diff = now - then;
  const mins = Math.floor(diff / 60_000);
  let rel: string;
  if (mins < 1) rel = 'just now';
  else if (mins < 60) rel = `${mins}m ago`;
  else {
    const hours = Math.floor(mins / 60);
    if (hours < 24) rel = `${hours}h ago`;
    else {
      const days = Math.floor(hours / 24);
      rel = days < 7 ? `${days}d ago` : new Date(iso).toLocaleDateString();
    }
  }
  return { rel, abs: iso };
}

// ─── Component ───────────────────────────────────────────────────────

/**
 * Compact access-request state card. Renders in the doc-detail page
 * above the tab strip; skips rendering entirely for non-access-request
 * docs (guard: `nest/external-clients` tag).
 *
 * Layout (three-column):
 *   Column 1 — state chip + client kind icon + client kind label + role handle
 *   Column 2 — key timestamps (requested_at, approved_at, role_last_changed_at)
 *   Column 3 — audit lineage (linked_pat_prefix, source_tag, requested_by_pat_id)
 *
 * Deprecated role handles get a warning banner beneath the card
 * suggesting the replacement handle.
 */
export function AccessRequestSummaryCard({
  tagNames,
  customProperties: cp,
  onNavigate,
}: AccessRequestSummaryCardProps) {
  // Guard: only render for External Clients Nest docs.
  if (!tagNames.includes('nest/external-clients')) return null;

  const { state, isElevated } = deriveState(tagNames);
  const StateIcon = ACCESS_STATE_ICON[state];
  const stateColor = isElevated && state === 'approved'
    ? ACCESS_STATE_COLOR.elevated
    : ACCESS_STATE_COLOR[state];

  const clientKind = typeof cp.client_kind === 'string' ? cp.client_kind : '';
  const ClientKindIcon = CLIENT_KIND_ICON[clientKind] ?? Bot;
  const clientKindLabel = CLIENT_KIND_LABEL[clientKind] ?? clientKind ?? '—';

  const roleHandle = typeof cp.role_handle === 'string' ? cp.role_handle : null;
  const roleVersion = typeof cp.role_version === 'string' ? cp.role_version : null;

  // Timestamps
  const requestedAt = relativeTime(cp.requested_at);
  const approvedAt = relativeTime(cp.approved_at);
  const roleLastChangedAt = relativeTime(cp.role_last_changed_at);
  const deniedAt = relativeTime(cp.denied_at);
  // CTSK-193 (2026-07-22): revoked_at previously not read here. Added
  // alongside the new "Revoked" timeline row + actor labels.
  const revokedAt = relativeTime(cp.revoked_at);

  // Audit lineage
  const sourceTag = displayCp(cp.source_tag);
  const linkedPatId = displayCp(cp.linked_pat_id);
  const requestedByPatId = displayCp(cp.requested_by_pat_id);
  const denialReason = typeof cp.denial_reason === 'string' ? cp.denial_reason : null;
  // CTSK-193: revocation_reason (from CTSK-186 API enrichment).
  const revocationReason = typeof cp.revocation_reason === 'string' ? cp.revocation_reason : null;

  // CTSK-193: actor display names (from CTSK-186 API enrichment). When
  // this component consumes the /nests/:slug/access-requests endpoint
  // (post-CTSK-193 ExternalClientsNest migration), display_name is
  // server-enriched from users.display_name. Legacy callers using raw
  // /documents get null here → fall back to _by_user_id ULID.
  const approvedByLabel = displayActor(
    cp.approved_by_display_name,
    cp.approved_by_user_id,
  );
  const deniedByLabel = displayActor(
    cp.denied_by_display_name,
    cp.denied_by_user_id,
  );
  const revokedByLabel = displayActor(
    cp.revoked_by_display_name,
    cp.revoked_by_user_id,
  );
  const roleChangedByLabel = displayActor(
    cp.role_last_changed_by_display_name,
    cp.role_last_changed_by_user_id,
  );

  // Previous PAT rotations — parse JSON-encoded string array or plain array.
  let previousPatCount = 0;
  const pp = cp.previous_pat_ids;
  if (Array.isArray(pp)) previousPatCount = pp.length;
  else if (typeof pp === 'string' && pp.length > 0) {
    try { const parsed = JSON.parse(pp); if (Array.isArray(parsed)) previousPatCount = parsed.length; } catch { /* ignore */ }
  }

  // Granted + dropped capabilities (Part B/9). Same JSON-encoded-string-
  // or-array shape trick as previous_pat_ids.
  const parseCapArray = (v: unknown): string[] | null => {
    if (Array.isArray(v)) return v.filter((c): c is string => typeof c === 'string');
    if (typeof v === 'string' && v.length > 0) {
      try { const parsed = JSON.parse(v); if (Array.isArray(parsed)) return parsed.filter((c): c is string => typeof c === 'string'); } catch { /* ignore */ }
    }
    return null;
  };
  const grantedCaps = parseCapArray(cp.granted_capabilities);
  const droppedCaps = parseCapArray(cp.dropped_capabilities);

  return (
    <section
      role="region"
      aria-label="Access request summary"
      style={{
        margin: '12px 0 16px',
        padding: 16,
        background: 'color-mix(in srgb, var(--accent-primary) 4%, var(--surface-raised))',
        border: `1px solid color-mix(in srgb, ${stateColor} 30%, var(--border-subtle))`,
        borderLeftWidth: 4,
        borderLeftColor: stateColor,
        borderRadius: 'var(--radius-md)',
        display: 'grid',
        gridTemplateColumns: 'minmax(220px, 1fr) minmax(220px, 1fr) minmax(220px, 1fr)',
        gap: 24,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      {/* Column 1 — state + kind + role */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <KeyRound size={14} style={{ color: 'var(--text-muted)' }} aria-hidden="true" />
          <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--text-muted)' }}>
            Access request
          </span>
          {onNavigate && (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); onNavigate('/mission-control/nests/external-clients'); }}
              style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--accent-primary)', textDecoration: 'none' }}
            >
              See in Nest →
            </a>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 12,
              background: stateColor + '20',
              color: stateColor,
              fontSize: 11, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}
          >
            <StateIcon size={11} aria-hidden="true" />
            {state}
            {isElevated && state === 'approved' && (
              <span style={{ marginLeft: 4, opacity: 0.85 }}>· elevated</span>
            )}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
          <ClientKindIcon size={14} style={{ color: 'var(--text-secondary)' }} aria-hidden="true" />
          <span>{clientKindLabel}</span>
        </div>

        <div style={{ fontSize: 13 }}>
          <span style={{ color: 'var(--text-muted)' }}>Role: </span>
          {roleHandle ? (
            <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
              {roleHandle}
              {roleVersion && <span style={{ color: 'var(--text-muted)' }}> · v{roleVersion}</span>}
            </code>
          ) : (
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>(none set)</span>
          )}
        </div>
      </div>

      {/* Column 2 — timestamps */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--text-muted)' }}>
          Timeline
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 12 }}>
          <span style={{ color: 'var(--text-muted)' }}>Requested</span>
          <span title={requestedAt.abs}>{requestedAt.rel}</span>
          {cp.approved_at !== undefined && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>Approved</span>
              <span title={approvedAt.abs}>
                {approvedAt.rel}
                {/* CTSK-193: actor from CTSK-186 API enrichment (or ULID fallback) */}
                {approvedByLabel && (
                  <span style={{ color: 'var(--text-muted)' }}> by {approvedByLabel}</span>
                )}
              </span>
            </>
          )}
          {cp.role_last_changed_at !== undefined && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>Role changed</span>
              <span title={roleLastChangedAt.abs}>
                {roleLastChangedAt.rel}
                {roleChangedByLabel && (
                  <span style={{ color: 'var(--text-muted)' }}> by {roleChangedByLabel}</span>
                )}
              </span>
            </>
          )}
          {cp.denied_at !== undefined && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>Denied</span>
              <span title={deniedAt.abs}>
                {deniedAt.rel}
                {deniedByLabel && (
                  <span style={{ color: 'var(--text-muted)' }}> by {deniedByLabel}</span>
                )}
                {denialReason && (
                  <div style={{ fontStyle: 'italic', color: 'var(--text-muted)', marginTop: 2 }}>
                    — {denialReason}
                  </div>
                )}
              </span>
            </>
          )}
          {/* CTSK-193: new Revoked row (previously not rendered in this
              component even though CTSK-181 shipped the revoke action) */}
          {cp.revoked_at !== undefined && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>Revoked</span>
              <span title={revokedAt.abs}>
                {revokedAt.rel}
                {revokedByLabel && (
                  <span style={{ color: 'var(--text-muted)' }}> by {revokedByLabel}</span>
                )}
                {revocationReason && (
                  <div style={{ fontStyle: 'italic', color: 'var(--text-muted)', marginTop: 2 }}>
                    — {revocationReason}
                  </div>
                )}
              </span>
            </>
          )}
          {previousPatCount > 0 && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>Rotations</span>
              <span>{previousPatCount}</span>
            </>
          )}
        </div>
      </div>

      {/* Column 3 — audit lineage */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--text-muted)' }}>
          Audit lineage
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 12 }}>
          <span style={{ color: 'var(--text-muted)' }}>Source tag</span>
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{sourceTag}</code>
          <span style={{ color: 'var(--text-muted)' }}>Linked PAT</span>
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{linkedPatId}</code>
          <span style={{ color: 'var(--text-muted)' }}>Requested by PAT</span>
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{requestedByPatId}</code>
          {typeof cp.bundle_version === 'string' && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>Bundle</span>
              <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{cp.bundle_version}</code>
            </>
          )}
        </div>
      </div>

      {denialReason && (
        <div
          style={{
            gridColumn: '1 / -1',
            marginTop: 4,
            padding: 10,
            background: 'color-mix(in srgb, var(--danger-primary, #EF4444) 8%, transparent)',
            border: '1px solid color-mix(in srgb, var(--danger-primary, #EF4444) 40%, var(--border-subtle))',
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          <strong style={{ color: 'var(--danger-primary, #EF4444)' }}>Denial reason: </strong>
          {denialReason}
        </div>
      )}

      {/* Granted / dropped capabilities — Part B/9. Shown only when the
          adapter has stamped them (approved + change-role docs). */}
      {(grantedCaps || droppedCaps) && (
        <div
          style={{
            gridColumn: '1 / -1',
            marginTop: 4,
            paddingTop: 12,
            borderTop: '1px dashed color-mix(in srgb, var(--border-subtle) 60%, transparent)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 24,
            fontSize: 12,
          }}
        >
          {grantedCaps && (
            <details style={{ flex: '1 1 300px', minWidth: 260 }}>
              <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={12} style={{ color: ACCESS_STATE_COLOR.approved }} aria-hidden="true" />
                <strong>Granted capabilities</strong>
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                  ({grantedCaps.length})
                </span>
              </summary>
              <div style={{ marginTop: 6, marginLeft: 18, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {grantedCaps.length === 0 ? (
                  <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(none — PAT holds zero caps)</span>
                ) : (
                  grantedCaps.map((c) => (
                    <code
                      key={c}
                      style={{
                        fontFamily: 'var(--font-mono)', fontSize: 11,
                        padding: '2px 6px', borderRadius: 4,
                        background: 'color-mix(in srgb, ' + ACCESS_STATE_COLOR.approved + ' 12%, transparent)',
                        color: ACCESS_STATE_COLOR.approved,
                        border: '1px solid color-mix(in srgb, ' + ACCESS_STATE_COLOR.approved + ' 40%, transparent)',
                      }}
                    >
                      {c}
                    </code>
                  ))
                )}
              </div>
            </details>
          )}
          {droppedCaps && droppedCaps.length > 0 && (
            <details style={{ flex: '1 1 300px', minWidth: 260 }}>
              <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={12} style={{ color: 'var(--warning-primary, #F59E0B)' }} aria-hidden="true" />
                <strong>Dropped capabilities</strong>
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                  ({droppedCaps.length}) — approver couldn't grant
                </span>
              </summary>
              <div style={{ marginTop: 6, marginLeft: 18, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {droppedCaps.map((c) => (
                  <code
                    key={c}
                    style={{
                      fontFamily: 'var(--font-mono)', fontSize: 11,
                      padding: '2px 6px', borderRadius: 4,
                      background: 'color-mix(in srgb, var(--warning-primary, #F59E0B) 12%, transparent)',
                      color: 'var(--warning-primary, #F59E0B)',
                      border: '1px solid color-mix(in srgb, var(--warning-primary, #F59E0B) 40%, transparent)',
                    }}
                  >
                    {c}
                  </code>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </section>
  );
}
